import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { useBarIndicatorsColors } from '@/composables/useBarIndicatorsColors';
import { Palette } from '@/themes/themeOverrides/Palette';
import { formatNumber } from '@/helpers/format/formatNumber.js';
import { getActiveBarIndex, toBarSegments } from '@/helpers/analytics/charts.js';
import { styled } from '@mui/material/styles';
import { analyticsStyles } from '@/themes/commonStyles/analytics';

const colorGray = Palette.gray.gray300;

const StyledAnalyticsVerticalBars = styled(Box)(() => analyticsStyles.horizontalBars);

const ContractorAvgAppRateField = ({ record }) => {
    const transactionsAppRate = record?.transactionsAppRate ?? 0;
    const transactionsAvg = record?.transactionsAppRateAvg ?? 0;
    const transactionsStdDev = record?.transactionsAppRateStdDev ?? 0;

    const lower = Math.max(0, transactionsAvg - transactionsStdDev);
    const upper = Math.min(1, transactionsAvg + transactionsStdDev);
    const breakpoints = [lower, transactionsAvg, upper];

    const barColors = useBarIndicatorsColors(transactionsAppRate, breakpoints, true);
    const activeBarIndex = getActiveBarIndex(transactionsAppRate, breakpoints);
    const barSegments = toBarSegments(barColors);

    return (
        <StyledAnalyticsVerticalBars>
            <Box className='bars-wrapper'>
                {barSegments.map((segment, barPosition) => (
                    <Box
                        key={segment.key}
                        className='bar'
                        sx={{
                            backgroundColor: barPosition === activeBarIndex ? segment.color : colorGray,
                        }}
                    />
                ))}
            </Box>
            <Box className='text-wrapper'>
                <Box
                    sx={{
                        color: transactionsAppRate <= lower ? Palette.red.red900 : Palette.black,
                    }}
                >
                    {(() => {
                        const percentage = transactionsAppRate * 100;
                        const hasFraction = Math.abs(percentage % 1) > 0;
                        return `${formatNumber(percentage, hasFraction ? 1 : 0)}%`;
                    })()}
                </Box>
            </Box>
        </StyledAnalyticsVerticalBars>
    );
};

ContractorAvgAppRateField.propTypes = {
    record: PropTypes.shape({
        transactionsAppRate: PropTypes.number,
        transactionsAppRateAvg: PropTypes.number,
        transactionsAppRateStdDev: PropTypes.number,
    }),
};

export default ContractorAvgAppRateField;
